import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogField, DialogFooter, dialogTextareaStyle } from "@/components/ui/Dialog";
import { Icon } from "@/components/ui/Icon";
import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { BRAND, statusColorOf } from "@/lib/tokens";
import { t } from "@/lib/strings";
import type { RowActionType, UUID, WorkspaceMember } from "@/lib/types";

/** A selectable status outcome for the resolve picker. */
export interface ActionOutcomeOption {
  id: string;
  label: string;
  color: string;
}

/** A notify-recipient candidate. Deliberately a minimal shape so BOTH workspace
 * members and board guests (cross-department people, e.g. the ticket creator)
 * satisfy it — resolving recipient names from board people, not members-only,
 * is what keeps a cross-dept creator from rendering as a "—" chip. */
export interface RecipientCandidate {
  user_id: UUID;
  display_name: string | null;
  email: string;
  username: string | null;
  department_workspace_id?: UUID | null;
  department_color?: string | null;
}

interface Spec {
  title: string;
  placeholder: string;
  cta: string;
  tone: "default" | "danger";
  // "leicht" transitions (Sichten/Einplanen/Zurückstellen): optional comment,
  // no recipient picker, no notification. The backend resolves the target
  // option from the action's plan slug, so no target_option_id is sent.
  leicht?: boolean;
}

const ACTION_SPECS: Record<RowActionType, Spec> = {
  senden_zur_pruefung: {
    title: t.actionDialogTitleSend,
    placeholder: t.actionBodyPlaceholderSend,
    cta: t.actionExecuteCta,
    tone: "default",
  },
  annehmen: {
    title: t.actionDialogTitleClaim,
    placeholder: t.actionBodyPlaceholderClaim,
    cta: t.actionExecuteCta,
    tone: "default",
  },
  wartet_auf_kunde: {
    title: t.actionDialogTitleWait,
    placeholder: t.actionBodyPlaceholderWait,
    cta: t.actionExecuteCta,
    tone: "default",
  },
  loesen: {
    title: t.actionDialogTitleResolve,
    placeholder: t.actionBodyPlaceholderResolve,
    cta: t.actionResolveCta,
    tone: "default",
  },
  bestaetigen: {
    title: t.actionDialogTitleConfirm,
    placeholder: t.actionBodyPlaceholderConfirm,
    cta: t.actionExecuteCta,
    tone: "default",
  },
  erneut_oeffnen: {
    title: t.actionDialogTitleReopen,
    placeholder: t.actionBodyPlaceholderReopen,
    cta: t.actionReopenCta,
    tone: "danger",
  },
  sichten: {
    title: t.actionDialogTitleSichten,
    placeholder: t.actionBodyPlaceholderSichten,
    cta: t.actionExecuteCta,
    tone: "default",
    leicht: true,
  },
  einplanen: {
    title: t.actionDialogTitleEinplanen,
    placeholder: t.actionBodyPlaceholderEinplanen,
    cta: t.actionExecuteCta,
    tone: "default",
    leicht: true,
  },
  in_review: {
    title: t.actionDialogTitleInReview,
    placeholder: t.actionBodyPlaceholderInReview,
    cta: t.actionExecuteCta,
    tone: "default",
  },
  zurueckstellen: {
    title: t.actionDialogTitleZurueckstellen,
    placeholder: t.actionBodyPlaceholderZurueckstellen,
    cta: t.actionExecuteCta,
    tone: "default",
    leicht: true,
  },
  zuweisen: {
    title: t.actionDialogTitleAssign,
    placeholder: t.actionBodyPlaceholderAssign,
    cta: t.actionExecuteCta,
    tone: "default",
  },
  hinfaellig: {
    title: t.actionDialogTitleHinfaellig,
    placeholder: t.actionBodyPlaceholderHinfaellig,
    cta: t.actionHinfaelligCta,
    tone: "danger",
  },
  eskalieren: {
    title: t.actionDialogTitleEskalieren,
    placeholder: t.actionBodyPlaceholderEskalieren,
    cta: t.actionEskalierenCta,
    tone: "default",
  },
  annehmen_idee: {
    title: t.actionDialogTitleAcceptIdea,
    placeholder: t.actionBodyPlaceholderAcceptIdea,
    cta: t.actionExecuteCta,
    tone: "default",
  },
  ablehnen_idee: {
    title: t.actionDialogTitleRejectIdea,
    placeholder: t.actionBodyPlaceholderRejectIdea,
    cta: t.actionExecuteCta,
    tone: "danger",
  },
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: RowActionType | null;
  defaultRecipients: UUID[];
  candidateMembers: RecipientCandidate[];
  busy: boolean;
  onSubmit: (payload: {
    body: string;
    recipient_user_ids: UUID[];
    target_option_id?: string;
    assignee_user_ids?: UUID[];
  }) => void;
  /** Closed-role outcomes offered for the resolve action. When 2+ are present
   * on a "loesen" action, the dialog shows an outcome picker (Gelöst vs. Nicht
   * relevant). Omitted/fewer than 2 → no picker, resolves to the role default. */
  outcomeOptions?: ActionOutcomeOption[];
  /** Pre-selected outcome (e.g. the kanban lane the card was dropped on). */
  defaultOutcomeId?: string | null;
  /** Annehmen: who the actor may assign the ticket to (self for members; the
   * whole department for owners/admins). Shows a required assignee picker. */
  assigneeOptions?: WorkspaceMember[];
  defaultAssigneeId?: UUID | null;
}

export function ActionDialog({
  open,
  onOpenChange,
  action,
  defaultRecipients,
  candidateMembers,
  busy,
  onSubmit,
  outcomeOptions,
  defaultOutcomeId,
  assigneeOptions,
  defaultAssigneeId,
}: Props) {
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState<UUID[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [outcomeId, setOutcomeId] = useState<string | null>(null);
  const [assigneeId, setAssigneeId] = useState<UUID | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // The outcome picker only appears when resolving AND the board offers more
  // than one closed outcome — otherwise the role default is unambiguous.
  const showOutcomePicker = action === "loesen" && (outcomeOptions?.length ?? 0) >= 2;
  // Annehmen + Zuweisen require picking an assignee (members only see themselves).
  const showAssignee =
    (action === "annehmen" || action === "zuweisen") && (assigneeOptions?.length ?? 0) > 0;

  // Initialize the form ONLY when the dialog opens or the action changes —
  // NOT when the array props (defaultRecipients/outcomeOptions/assigneeOptions)
  // get fresh identities on a parent re-render. The parent rebuilds those arrays
  // every render, so depending on them re-ran this effect mid-edit and wiped the
  // user's typed text (e.g. when a query resolved or the 30s poll fired). The
  // seed props are read at open-time; that's intentional, hence the disable.
  useEffect(() => {
    if (!open) return;
    setBody("");
    setRecipients(defaultRecipients);
    setPickerOpen(false);
    setOutcomeId(defaultOutcomeId ?? outcomeOptions?.[0]?.id ?? null);
    setAssigneeId(defaultAssigneeId ?? assigneeOptions?.[0]?.user_id ?? null);
    const tid = window.setTimeout(() => textareaRef.current?.focus(), 30);
    return () => window.clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action]);

  const spec = action ? ACTION_SPECS[action] : null;
  const isLeicht = spec?.leicht ?? false;
  const byId = useMemo(
    () => new Map(candidateMembers.map((m) => [m.user_id, m])),
    [candidateMembers],
  );

  function handleSubmit() {
    if (!action || !spec) return;
    const trimmed = body.trim();
    // leicht transitions may have an empty comment; schwer actions require one.
    if (!isLeicht && !trimmed) return;
    if (showAssignee && !assigneeId) return;
    // Recipients are optional — a self-action (e.g. resolving your own ticket)
    // has no one to notify; the status change + comment still happen. leicht
    // transitions notify no one.
    onSubmit({
      body: trimmed,
      recipient_user_ids: isLeicht ? [] : recipients,
      target_option_id: showOutcomePicker ? (outcomeId ?? undefined) : undefined,
      assignee_user_ids: showAssignee && assigneeId ? [assigneeId] : undefined,
    });
  }

  function toggleRecipient(id: UUID) {
    setRecipients((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title={spec?.title ?? ""}
      width={520}
    >
      {showAssignee ? (
        <DialogField
          label={t.actionAssignLabel}
          htmlFor="action-assignee"
          hint={t.actionAssignHint}
        >
          <div id="action-assignee" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(assigneeOptions ?? []).map((m) => {
              const selected = m.user_id === assigneeId;
              const name = m.display_name ?? m.email;
              return (
                <button
                  key={m.user_id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setAssigneeId(m.user_id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "5px 11px 5px 5px",
                    borderRadius: 999,
                    cursor: "pointer",
                    fontSize: 13,
                    color: "var(--ink-1)",
                    background: selected ? "var(--surface-soft)" : "var(--surface)",
                    border: `1px solid ${selected ? BRAND.blue : "var(--hairline-strong)"}`,
                  }}
                >
                  <Avatar
                    user={{
                      id: m.user_id,
                      display_name: name,
                      initials: deriveInitials(name),
                      avatar_color: deriveAvatarColor(m.user_id),
                      username: m.username ?? null,
                      department_workspace_id: m.department_workspace_id ?? null,
                      department_color: m.department_color ?? null,
                    }}
                    size={20}
                  />
                  {name}
                </button>
              );
            })}
          </div>
        </DialogField>
      ) : null}

      <DialogField label={t.actionBodyLabel} htmlFor="action-body">
        <textarea
          ref={textareaRef}
          id="action-body"
          rows={4}
          placeholder={spec?.placeholder ?? ""}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          style={{ ...dialogTextareaStyle, height: 96 }}
        />
      </DialogField>

      {showOutcomePicker ? (
        <DialogField label={t.actionResolveOutcomeLabel} htmlFor="action-outcome">
          <div id="action-outcome" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(outcomeOptions ?? []).map((opt) => {
              const selected = opt.id === outcomeId;
              const tint = statusColorOf(opt.color);
              return (
                <button
                  key={opt.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setOutcomeId(opt.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "6px 12px",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 500,
                    color: selected ? tint.fg : "var(--ink-2)",
                    background: selected ? tint.bg : "var(--surface)",
                    border: `1px solid ${selected ? tint.dot : "var(--hairline-strong)"}`,
                  }}
                >
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: 999,
                      background: tint.dot,
                      flexShrink: 0,
                    }}
                  />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </DialogField>
      ) : null}

      {!isLeicht ? (
        <DialogField
          label={t.actionRecipientsLabel}
          htmlFor="action-recipients"
          hint={t.actionRecipientsCount.replace("{count}", String(recipients.length))}
        >
          <div
            id="action-recipients"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              padding: 8,
              borderRadius: 7,
              border: "1px solid var(--hairline-strong)",
              background: "var(--surface)",
              minHeight: 36,
            }}
          >
            {recipients.map((uid) => {
              const m = byId.get(uid);
              const name = m?.display_name ?? m?.email ?? "—";
              return (
                <span
                  key={uid}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 8px 3px 4px",
                    borderRadius: 999,
                    background: "var(--surface-soft)",
                    fontSize: 12,
                  }}
                >
                  <Avatar
                    user={{
                      id: uid,
                      display_name: name,
                      initials: deriveInitials(name),
                      avatar_color: deriveAvatarColor(uid),
                      username: m?.username ?? null,
                      department_workspace_id: m?.department_workspace_id ?? null,
                      department_color: m?.department_color ?? null,
                    }}
                    size={18}
                  />
                  {name}
                  <button
                    type="button"
                    onClick={() => toggleRecipient(uid)}
                    aria-label={t.actionRecipientRemove}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--ink-3)",
                      padding: 0,
                      display: "inline-flex",
                    }}
                  >
                    <Icon name="x" size={11} />
                  </button>
                </span>
              );
            })}
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              style={{
                padding: "3px 8px",
                borderRadius: 999,
                border: "1px dashed var(--hairline-strong)",
                background: "transparent",
                fontSize: 12,
                cursor: "pointer",
                color: "var(--ink-2)",
              }}
            >
              + {t.actionRecipientsAddMore}
            </button>
          </div>

          {pickerOpen ? (
            <div
              role="listbox"
              style={{
                marginTop: 6,
                borderRadius: 7,
                border: "1px solid var(--hairline)",
                background: "var(--surface)",
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {candidateMembers.length === 0 ? (
                <div
                  style={{
                    padding: "10px 12px",
                    color: "var(--ink-4)",
                    fontSize: 12,
                  }}
                >
                  {t.actionRecipientsNoOthers}
                </div>
              ) : (
                candidateMembers.map((m) => {
                  const checked = recipients.includes(m.user_id);
                  const name = m.display_name ?? m.email;
                  return (
                    <button
                      key={m.user_id}
                      role="option"
                      aria-selected={checked}
                      type="button"
                      onClick={() => toggleRecipient(m.user_id)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 10px",
                        background: checked ? "var(--surface-soft)" : "transparent",
                        border: "none",
                        borderBottom: "1px solid var(--hairline-soft)",
                        cursor: "pointer",
                        textAlign: "left",
                        fontSize: 13,
                        color: "var(--ink-1)",
                      }}
                    >
                      <Avatar
                        user={{
                          id: m.user_id,
                          display_name: name,
                          initials: deriveInitials(name),
                          avatar_color: deriveAvatarColor(m.user_id),
                          username: m.username ?? null,
                          department_workspace_id: m.department_workspace_id ?? null,
                          department_color: m.department_color ?? null,
                        }}
                        size={22}
                      />
                      <span style={{ flex: 1 }}>{name}</span>
                      {checked ? <Icon name="check" size={14} /> : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </DialogField>
      ) : null}

      <DialogFooter>
        <Button variant="ghost" size="md" disabled={busy} onClick={() => onOpenChange(false)}>
          {t.cancel}
        </Button>
        <Button
          variant={spec?.tone === "danger" ? "destructive" : "primary"}
          size="md"
          disabled={busy || (!isLeicht && !body.trim()) || (showAssignee && !assigneeId)}
          onClick={handleSubmit}
        >
          {busy ? t.loading : (spec?.cta ?? "")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
